import { useEffect, useState } from "react";

/** Dedupe verify for the same Paystack reference (Strict Mode double effect, slow navigation). */
const premiumVerifyStarted = new Set<string>();
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { StatTile } from "@/components/dashboard/shared/StatTile";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { isPaidAccess, planLabel } from "@/lib/plan";
import { toast } from "sonner";
import { Loader2, Crown, Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

type SubResponse = {
  plan: string;
  subscription?: {
    lastInterval?: string;
    premiumExpiresAt?: string;
    lastPaymentReference?: string;
    paystackCustomerCode?: string;
  };
  isPaid?: boolean;
};

export function BillingPage({ embedded = false }: { embedded?: boolean }) {
  const { user, currentStore } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);

  const ownerOnly = currentStore?.isOwner === true;

  const { data, isLoading } = useQuery<SubResponse>({
    queryKey: ["subscription-current"],
    queryFn: async () => {
      const { data } = await api.get("/api/subscriptions/current");
      return data;
    },
    enabled: ownerOnly,
  });

  // String deps only, avoid `searchParams` object identity churn from react-router.
  const paystackReturn = searchParams.get("paystack");
  const verifyReference =
    searchParams.get("reference") || searchParams.get("trxref");

  useEffect(() => {
    if (!paystackReturn || !verifyReference || !ownerOnly) return;
    if (premiumVerifyStarted.has(verifyReference)) return;
    premiumVerifyStarted.add(verifyReference);

    let cancelled = false;
    setVerifying(true);
    (async () => {
      let willReload = false;
      try {
        await api.get("/api/subscriptions/verify", {
          params: { reference: verifyReference },
        });
        // Do not gate success on `cancelled`, Strict Mode unmounts the first
        // effect while the request is in flight; we still must redirect once.
        toast.success("Payment verified, Premium extended");
        willReload = true;
        window.location.replace(
          `${window.location.pathname}${window.location.hash || ""}`
        );
      } catch (e: unknown) {
        premiumVerifyStarted.delete(verifyReference);
        if (!cancelled) {
          const msg =
            (e as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || "Could not verify payment";
          toast.error(msg);
        }
      } finally {
        if (!cancelled && !willReload) {
          setVerifying(false);
          setSearchParams({}, { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [paystackReturn, verifyReference, ownerOnly, setSearchParams]);

  const initMutation = useMutation({
    mutationFn: async (interval: "monthly" | "yearly") => {
      const { data } = await api.post("/api/subscriptions/initialize", {
        interval,
      });
      return data as { authorizationUrl: string };
    },
    onSuccess: (d) => {
      if (d.authorizationUrl) {
        window.location.href = d.authorizationUrl;
      }
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not start checkout";
      toast.error(msg);
    },
  });

  if (!ownerOnly) {
    return (
      <div className="space-y-6">
        {embedded ? null : <DashboardHead pageName="Billing" />}
        {embedded ? null : <PageHeader title="Billing" />}
        <Alert>
          <Mail className="h-4 w-4" />
          <AlertTitle>Store owner only</AlertTitle>
          <AlertDescription>
            Only the store owner can manage billing and subscriptions.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const paid = isPaidAccess(user) || data?.isPaid;
  const sub = data?.subscription;

  return (
    <div className="space-y-6">
      {embedded ? null : <DashboardHead pageName="Billing" />}
      {embedded ? null : (
        <PageHeader
          title="Billing"
          subtitle="Extend Premium when you need it"
        />
      )}

      {(verifying || isLoading) && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {verifying ? "Verifying payment…" : "Loading subscription…"}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <SectionCard className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-heading text-[11px] font-semibold uppercase tracking-[0.14em] text-primary/55">Current plan</p>
              <p className="text-2xl font-bold font-heading flex items-center gap-2 mt-1">
                {planLabel(user?.plan)}
                {paid && (
                  <Badge variant="default" className="gap-1">
                    <Crown className="h-3 w-3" /> Active
                  </Badge>
                )}
              </p>
              {user?.plan === "custom" && (
                <p className="text-sm text-muted-foreground mt-2">
                  Your custom plan was set by our team. Contact support for
                  changes.
                </p>
              )}
            </div>
          </div>

          {sub?.premiumExpiresAt && user?.plan === "premium" && (
            <p className="text-sm mt-4 text-muted-foreground">
              Premium access until{" "}
              {new Date(sub.premiumExpiresAt).toLocaleDateString("en-NG", {
                dateStyle: "medium",
              })}
              {sub.lastInterval ? ` · last paid ${sub.lastInterval}` : ""}
            </p>
          )}
        </SectionCard>

        <SectionCard className="p-6 space-y-4">
          <div>
            <p className="text-sm font-semibold">Premium</p>
            <p className="text-sm text-muted-foreground mt-1">
              ₦5,000 / month or ₦45,000 / year (save vs monthly). Unlimited
              products, analytics, store managers, and bulk upload.
            </p>
          </div>
          {!paid && user?.plan !== "custom" && (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                disabled={initMutation.isPending}
                onClick={() => initMutation.mutate("monthly")}
              >
                {initMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Pay monthly"
                )}
              </Button>
              <Button
                variant="outline"
                disabled={initMutation.isPending}
                onClick={() => initMutation.mutate("yearly")}
              >
                Pay yearly
              </Button>
            </div>
          )}
          {paid && user?.plan === "premium" && (
            <p className="text-xs text-muted-foreground">
              Renew anytime from this page to extend your access. 
            </p>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatTile
          label="Monthly price"
          value="₦5,000"
          hint="Billed every month via Paystack"
        />
        <StatTile
          label="Yearly price"
          value="₦45,000"
          hint="Best value, save vs paying monthly"
        />
      </div>

      
    </div>
  );
}
